/* ═══════════════════════════════════════════════════════════════
   FormaService — Standard Configs + Custom Tokens via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';

const formaOpts: TableOpts = {
  jsonFields: [
    'docSettings',
    'levels',
    'headerFooter',
    'special',
    'images',
    'editHistory',
    'placeholders',
    'bindings',
  ],
};

const tokenOpts: TableOpts = {};

export interface CustomTokenRecord {
  id: string;
  tokens: string[];
}

export const FormaService = {
  /* ── Formas (Standard Configs) ── */
  list: async (): Promise<StandardConfig[]> =>
    cache.list<StandardConfig>('standard_configs', formaOpts),

  /** `collabMirror`: the Studio's Publish write - the shared draft's content
   *  going out to the published columns. The server reads the flag as "this
   *  is a publish" (needs formas.publish) and keeps the shared draft alive;
   *  an unflagged write of those columns resets it (Discard draft, import). */
  save: async (cfg: StandardConfig, o?: { collabMirror?: boolean }): Promise<StandardConfig> => {
    // `draftDirty` is derived by the server on reads - never written back.
    const { draftDirty: _derived, ...rest } = cfg;
    void _derived;
    const clean = rest as StandardConfig;
    const saved = await cache.save(
      'standard_configs',
      o?.collabMirror ? ({ ...clean, collabMirror: true } as StandardConfig) : clean,
      formaOpts
    );
    // The wire flag must not linger on the cached row.
    delete (saved as unknown as Record<string, unknown>).collabMirror;
    return saved;
  },

  /** The row as the CLOUD has it right now — bypasses the in-memory cache.
   *  Whole-row saves assembled from UI-state copies silently revert every
   *  field some other surface saved in between (a rebuilt footer was lost
   *  exactly this way, 2026-08-19); single-field patches must start here. */
  getFresh: async (id: string): Promise<StandardConfig | undefined> => {
    cache.invalidate('standard_configs');
    const all = await cache.list<StandardConfig>('standard_configs', formaOpts);
    return all.find((f) => f.id === id);
  },

  delete: async (id: string): Promise<void> => cache.remove('standard_configs', id),

  /** Archive or restore — a TWO-key partial update on purpose: the server
   *  reads an `archived_at` flip of three keys or fewer as an archive toggle
   *  and gates it on `formas.archive`. Mirrors ModuleService.setArchived;
   *  standard_configs has no modified_by/last_modified column, so only the
   *  archive pair is sent (migration 050). */
  setArchived: async (id: string, archived: boolean, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      'standard_configs',
      {
        archived_at: archived ? new Date().toISOString() : null,
        archived_by: archived ? userName || null : null,
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate('standard_configs');
    logActivity({
      action: archived ? 'forma.archive' : 'forma.restore',
      entityType: 'forma',
      entityId: id,
    });
  },

  /**
   * Specs-style application: applying a master forma to a project makes a
   * COPY that lives in the project — edits there touch only that copy, the
   * master stays a pristine template. Reuses the project's existing copy of
   * the same master (idempotent); no-op when handed a project forma already.
   */
  ensureProjectCopy: async (
    master: StandardConfig,
    project: { id: string; number?: string; name?: string }
  ): Promise<StandardConfig> => {
    if ((master as any).projectId) return master; // already project-scoped
    const all = await cache.list<StandardConfig>('standard_configs', formaOpts);
    const existing = all.find(
      (f: any) => f.projectId === project.id && !f.specId && f.sourceFormaId === master.id
    );
    if (existing) return existing;
    const copy: any = JSON.parse(JSON.stringify(master));
    copy.id = 'pf-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    copy.projectId = project.id;
    copy.sourceFormaId = master.id;
    delete copy.specId;
    delete copy.draftDirty; // derived from the MASTER's shared draft - not the copy's
    delete copy.editHistory;
    delete copy.attachedProjectId; // attachment is master metadata, not the copy's
    // The copy keeps the master's {P#} blanks AND their memories; what each one
    // MEANS is a project decision, so bindings start empty and the export gate
    // holds until someone fills them in (Project Tokens palette).
    copy.bindings = copy.bindings || {};
    const baseName = String(copy.docSettings?.standardName || 'Forma').replace(/ — .*$/, '');
    copy.docSettings = {
      ...copy.docSettings,
      standardName: `${baseName} — ${project.number || project.name || 'project'}`,
    };
    await cache.save('standard_configs', copy, formaOpts);
    return copy as StandardConfig;
  },

  /* ── Custom Tokens ── */
  loadTokens: async (): Promise<string[]> => {
    const rows = await cache.list<CustomTokenRecord>('custom_tokens', tokenOpts);
    if (rows.length > 0) return rows[0].tokens || [];
    return [];
  },

  saveTokens: async (tokens: string[]): Promise<void> => {
    const record: CustomTokenRecord = {
      id: 'firm-tokens',
      tokens,
    };
    await cache.save('custom_tokens', record, { jsonFields: ['tokens'] });
  },
};
